// src/utils/animalCategories.js
//
// Animal Products catalog for Orrith.
//
// This module is intentionally isolated from agriculturalCategories.js:
//  - Animal listings never leak into crop-only search, suggestions, or
//    expiration prediction.
//  - Each animal category declares its own quantity unit and pricing unit so
//    totals are always calculated from the selected unit, never assumed.
//  - No scientifically validated shelf-life predictions are invented here.
//   Sellers provide collection dates and best-before dates where appropriate.

const DEFAULT_COLOR = 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';

export const ANIMAL_PRODUCT_TYPES = {
  LIVE_ANIMAL: 'live_animal',
  ANIMAL_PRODUCT: 'animal_product',
};

export const ANIMAL_QUANTITY_UNITS = {
  BIRD: 'bird',
  EGG: 'egg',
  TRAY: 'tray',
};

export const ANIMAL_CATEGORIES = [
  {
    id: 'chickens',
    name: 'Chickens',
    productType: ANIMAL_PRODUCT_TYPES.LIVE_ANIMAL,
    quantityUnit: ANIMAL_QUANTITY_UNITS.BIRD,
    pricingUnit: ANIMAL_QUANTITY_UNITS.BIRD,
    requiresExpiry: false,
    allowsCollectionDate: false,
    allowsBestBefore: false,
    description: 'Live chickens for sale',
    examples: ['Kuroiler', 'Broiler', 'Layer', 'Rooster', 'Hen'],
    icon: 'FaEgg',
  },
  {
    id: 'eggs',
    name: 'Eggs',
    productType: ANIMAL_PRODUCT_TYPES.ANIMAL_PRODUCT,
    quantityUnit: ANIMAL_QUANTITY_UNITS.EGG,
    pricingUnit: ANIMAL_QUANTITY_UNITS.EGG,
    requiresExpiry: false,
    allowsCollectionDate: true,
    allowsBestBefore: true,
    description: 'Fresh eggs from chickens or other poultry',
    examples: ['Chicken Eggs', 'Duck Eggs', 'Turkey Eggs'],
    icon: 'FaEgg',
  },
];

const ANIMAL_CATEGORY_BY_ID = ANIMAL_CATEGORIES.reduce((acc, cat) => {
  acc[cat.id] = cat;
  return acc;
}, {});

const normalizeToken = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Resolve an animal category id from a raw id, name, or example token.
 * Returns null when the value does not match any animal category.
 */
export const resolveAnimalCategoryId = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const normalized = normalizeToken(raw);

  const exactById = ANIMAL_CATEGORIES.find((cat) => normalizeToken(cat.id) === normalized);
  if (exactById) return exactById.id;

  const exactByName = ANIMAL_CATEGORIES.find((cat) => normalizeToken(cat.name) === normalized);
  if (exactByName) return exactByName.id;

  const byExample = ANIMAL_CATEGORIES.find((cat) =>
    (cat.examples || []).some((ex) => normalizeToken(ex) === normalized)
  );
  return byExample ? byExample.id : null;
};

/**
 * Returns true when the given category id belongs to the animal catalog.
 */
export const isAnimalCategory = (categoryId) => {
  if (!categoryId) return false;
  return ANIMAL_CATEGORY_BY_ID[normalizeToken(categoryId)] != null;
};

export const getAnimalCategoryConfig = (categoryId) => {
  if (!categoryId) return null;
  return ANIMAL_CATEGORY_BY_ID[normalizeToken(categoryId)] || null;
};

export const getAnimalCategoryLabel = (categoryId, fallback = '') => {
  const cat = getAnimalCategoryConfig(categoryId);
  return cat ? cat.name : fallback;
};

/**
 * Search animal categories and examples by term. Used by the form's
 * animal-only suggestion dropdown. Returns [] for empty term.
 */
export const searchAnimalCategories = (query = '') => {
  const term = normalizeToken(query);
  if (!term) return ANIMAL_CATEGORIES;

  const results = [];
  for (const cat of ANIMAL_CATEGORIES) {
    let score = 0;
    if (normalizeToken(cat.id) === term) score += 200;
    if (normalizeToken(cat.name) === term) score += 180;
    if (normalizeToken(cat.name).startsWith(term)) score += 120;
    if (normalizeToken(cat.id).includes(term)) score += 80;
    const exampleScore = (cat.examples || []).reduce((max, ex) => {
      const t = normalizeToken(ex);
      if (t === term) return Math.max(max, 160);
      if (t.startsWith(term)) return Math.max(max, 110);
      if (t.includes(term)) return Math.max(max, 60);
      return max;
    }, 0);
    score += exampleScore;
    if (score > 0) results.push({ ...cat, score });
  }

  return results
    .sort((a, b) => b.score - a.score || a.name.localeCompare(b.name))
    .map(({ score, ...cat }) => cat);
};

/**
 * Animal Products hierarchy mirroring the shape of AGRICULTURAL_CATEGORY_HIERARCHY
 * so the existing FarmSecondaryNav / FarmMegaMenu components can render animal
 * listings without modification. Animal categories are kept isolated from the
 * crop catalog: they never appear in crop-only search or suggestions.
 *
 * Structure:
 *   macro → groups → categories → items (examples)
 */
export const ANIMAL_CATEGORY_HIERARCHY = [
  {
    id: 'animal-products',
    name: 'Animal Products',
    icon: 'FaEgg',
    groups: [
      {
        id: 'live-poultry',
        name: 'Live Poultry',
        categoryIds: ['chickens'],
      },
      {
        id: 'eggs-poultry',
        name: 'Eggs',
        categoryIds: ['eggs'],
      },
    ],
  },
];

const ANIMAL_MACRO_BY_ID = ANIMAL_CATEGORY_HIERARCHY.reduce((acc, macro) => {
  acc[macro.id] = macro;
  return acc;
}, {});

const ANIMAL_CATEGORY_TO_HIERARCHY = ANIMAL_CATEGORY_HIERARCHY.reduce((acc, macro) => {
  macro.groups.forEach((group) => {
    group.categoryIds.forEach((categoryId) => {
      if (!acc[categoryId]) {
        acc[categoryId] = {
          macroCategoryId: macro.id,
          macroCategoryName: macro.name,
          groupId: group.id,
          groupName: group.name,
        };
      }
    });
  });
  return acc;
}, {});

/**
 * Returns the hierarchy metadata for an animal category id, or null.
 */
export const getAnimalCategoryHierarchyMeta = (categoryId) =>
  ANIMAL_CATEGORY_TO_HIERARCHY[normalizeToken(categoryId)] || null;

/**
 * Returns the enriched animal hierarchy (with categories and items per group),
 * matching the shape produced by agriculturalCategories.enrichGroup.
 */
export const getAnimalCategoryHierarchy = () => {
  return ANIMAL_CATEGORY_HIERARCHY.map((macro) => ({
    ...macro,
    groups: macro.groups.map((group) => {
      const categories = group.categoryIds
        .map((id) => ANIMAL_CATEGORY_BY_ID[id])
        .filter(Boolean);
      const items = categories.flatMap((cat) => cat.examples || []);
      return {
        ...group,
        macroCategoryId: macro.id,
        macroCategoryName: macro.name,
        categories,
        items,
        primaryCategoryId: group.categoryIds[0] || '',
      };
    }),
  }));
};

export default {
  ANIMAL_PRODUCT_TYPES,
  ANIMAL_QUANTITY_UNITS,
  ANIMAL_CATEGORIES,
  ANIMAL_CATEGORY_HIERARCHY,
  resolveAnimalCategoryId,
  isAnimalCategory,
  getAnimalCategoryConfig,
  getAnimalCategoryLabel,
  searchAnimalCategories,
  getAnimalCategoryHierarchyMeta,
  getAnimalCategoryHierarchy,
};